// WHAT: Tab component for displaying all workspaces with their aspects
// WHY:  Provides overview of workspace configuration with color coding
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: useWorkspaceConfig hook
// RISKS:
//   - NOTE: Meta key filtering is hardcoded - may miss other meta keys
//   - NOTE: Color extraction may fail if aspect data structure is unexpected
//   - NOTE: No error handling for malformed config

import { useState, useEffect, useMemo } from "react";

import { Card } from "@/components/ui";

import { useWorkspaceConfig } from "../../hooks/time_tracking/useWorkspaceConfig";

/**
 * Workspace information interface
 */
interface WorkspaceInfo {
  /** List of aspect names */
  aspects: string[];
  /** Display color */
  color: string;
  /** Workspace name */
  name: string;
}

type WorkspaceConfig = Record<string, Record<string, { color?: string }>>;

const isWorkspaceConfig = (value: unknown): value is WorkspaceConfig => typeof value === "object";

/**
 * Extracts aspects from workspace data.
 *
 * @param workspaceData - Workspace data object
 *
 * @returns Array of aspect names
 *
 */
const extractAspects = (workspaceData: unknown): string[] => {
  const aspects: string[] = [];
  const typedWorkspaceData = workspaceData as Record<string, { color?: string }> | undefined;
  if (typeof typedWorkspaceData === "object") {
    for (const aspectName of Object.keys(typedWorkspaceData)) {
      aspects.push(aspectName);
    }
  }
  return aspects;
};

/**
 * Determines workspace color from first aspect or returns default.
 *
 * @param aspects - Array of aspect names
 *
 * @param workspaceData - Workspace data object
 *
 * @returns Color string
 *
 */
const getWorkspaceColor = (aspects: string[], workspaceData: unknown): string => {
  if (aspects.length === 0) {
    return "#6B7280";
  }
  const typedWorkspaceData = workspaceData as Record<string, { color?: string }> | undefined;
  if (typeof typedWorkspaceData === "object") {
    const firstAspect = aspects[0];
    if (typeof firstAspect === "string" && firstAspect in typedWorkspaceData) {
      const firstAspectDataRaw = typedWorkspaceData[firstAspect];
      if (
        firstAspectDataRaw &&
        typeof firstAspectDataRaw === "object" &&
        "color" in firstAspectDataRaw &&
        typeof firstAspectDataRaw.color === "string"
      ) {
        return firstAspectDataRaw.color;
      }
    }
  }
  return "#6B7280";
};

/**
 * WHAT:
 *   Tab component that displays all workspaces with their aspects and colors
 *   in a structured list view.
 *
 * WHY:
 *   Enables users to see all configured workspaces and their aspects for
 *   reference and management.
 *
 * BEHAVIOR:
 *   - Loads workspace configuration
 *   - Extracts workspace and aspect information
 *   - Filters out meta keys
 *   - Displays workspaces with aspect lists
 *   - Uses aspect colors for visual indication
 *   - Updates when config changes
 *
 * RISKS:
 *   - NOTE: Meta key filtering is hardcoded - may miss other meta keys
 *   - NOTE: Color extraction may fail if aspect data structure is unexpected
 *   - NOTE: No error handling for malformed config
 *
 * @returns JSX.Element
 *
 */
export const WorkspacesTab = () => {
  const [rawConfig, , isLoading] = useWorkspaceConfig() as [unknown, unknown, boolean];
  const config = useMemo<WorkspaceConfig>(() => {
    if (!isWorkspaceConfig(rawConfig)) {
      return {};
    }
    return rawConfig;
  }, [rawConfig]);
  const [workspaces, setWorkspaces] = useState<WorkspaceInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    try {
      if (Object.keys(config).length === 0) {
        setWorkspaces([]);
        return;
      }

      const workspaceList: WorkspaceInfo[] = [];

      // NOTE: Iterate through all workspaces in config
      for (const [workspaceName, workspaceData] of Object.entries(config)) {
        // NOTE: Filter out meta keys
        const metaKeys = ["settings", "Meta", "System", "Version"];
        if (metaKeys.includes(workspaceName)) {
          continue;
        }

        // NOTE: Extract aspects and their colors
        const aspects = extractAspects(workspaceData);

        // NOTE: Determine workspace main color (first aspect color or default)
        const workspaceColor = getWorkspaceColor(aspects, workspaceData);

        workspaceList.push({
          aspects: aspects,
          color: workspaceColor,
          name: workspaceName,
        });
      }

      setWorkspaces(workspaceList);
    } catch {
      setError("Fehler beim Laden der Workspaces");
    } finally {
      setLoading(false);
    }
  }, [config]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
        padding="lg"
        variant="elevated"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-tracq-bright-blue/20 flex items-center justify-center">
            <svg
              className="w-6 h-6 text-tracq-bright-blue"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-white">Workspaces</h3>
            <p className="text-slate-400 text-sm">Alle verfügbaren Workspaces und deren Aspekte</p>
          </div>
        </div>
      </Card>

      {/* Workspaces Liste */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
        padding="lg"
        variant="elevated"
      >
        {(() => {
          if (loading || isLoading) {
            return (
              <div className="h-40 flex items-center justify-center text-slate-500 animate-pulse">
                Loading…
              </div>
            );
          }
          if (error) {
            return (
              <div className="h-40 flex items-center justify-center text-red-400">{error}</div>
            );
          }
          if (workspaces.length === 0) {
            return (
              <div className="h-40 flex items-center justify-center text-slate-300">
                Keine Workspaces konfiguriert
              </div>
            );
          }
          return (
            <div className="space-y-4">
              {workspaces.map((workspace, index) => (
                <div
                  className="flex items-center gap-4 p-4 rounded-xl bg-slate-800/30 border border-slate-600/50 hover:bg-slate-800/50 transition-all duration-200"
                  key={workspace.name}
                >
                  {/* Workspace Info */}
                  <div className="flex items-center gap-4 flex-1">
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: workspace.color }}
                    />
                    <div>
                      <div className="text-lg font-semibold text-white">
                        {index}. {workspace.name}
                      </div>
                      <div className="text-sm text-slate-400">
                        {workspace.aspects.length} Aspekt
                        {workspace.aspects.length === 1 ? "" : "e"}
                      </div>
                    </div>
                  </div>

                  {/* Aspects Liste */}
                  <div className="flex flex-wrap gap-2">
                    {workspace.aspects.map((aspect) => (
                      <span
                        className="px-3 py-1 rounded-lg text-xs font-medium border"
                        key={aspect}
                        style={{
                          backgroundColor: `${workspace.color}20`,
                          borderColor: `${workspace.color}40`,
                          color: workspace.color,
                        }}
                      >
                        {aspect}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </Card>
    </div>
  );
};
